import { IStyleFunctionOrObject } from '@fluentui/merge-styles';
import { IContextualMenuItem } from '@fluentui/react';
import { IGroupedComboBoxSubMenuSection } from 'grouped-combobox-core';
import * as React from 'react';
import { IGroupedComboBoxItemStyleProps, IGroupedComboBoxItemStyles } from '..';
import { useAnimateListModifications } from '../utils/useAnimateListModifications';
import { createSubmenuItems } from './createSubmenuItems';

/**
 * This hook will make the button be hidden for 1 frame before transitioning to its visible state
 * @param enableAnimation - If false we'll skip the animation and just show the button
 * @returns Whether the button should be shown (visible and take up space)
 */
export function useAnimateEntrance(enableAnimation: boolean): boolean {
    // If animation is disabled, start with showButton True so we don't wait for the transition to happen
    const [showButton, setShowButton] = React.useState(!enableAnimation);

    // Delay showing the button by 1 frame in order for the transition to happen
    if (!showButton) {
        requestAnimationFrame(() => {
            setShowButton(true);
        });
    }

    return showButton;
}

function createEmptyItem(onRenderEmptyItem: () => JSX.Element): IContextualMenuItem {
    return { key: 'EMPTY_ITEM', onRender: onRenderEmptyItem };
}

function getContextualMenuItemKey(item: IContextualMenuItem): string {
    return item.key;
}

export function useAnimateSubmenuItemExit(
    isSubmenuOpen: boolean,
    itemHeight: number,
    submenuSections?: IGroupedComboBoxSubMenuSection[],
    selectedKey?: string,
    styles?: IStyleFunctionOrObject<IGroupedComboBoxItemStyleProps, IGroupedComboBoxItemStyles>
): IContextualMenuItem[] {
    const submenuItems = React.useMemo(
        () =>
            submenuSections && isSubmenuOpen
                ? createSubmenuItems(submenuSections, selectedKey, styles)
                : [],

        [submenuSections, styles, isSubmenuOpen, selectedKey]
    );

    // Since IContextualMenuItems handle their own rendering, we don't need any of the
    // empty item related return values here.
    // Also, since we can rely on the submenu items not being rerendered when the list contents change, we can allow them all
    // to animate on mount and we don't need the specific index
    const [
        itemsToRender,
        /* shouldAnimateEntrance */
        /* renderIfEmptyItem */
    ] = useAnimateListModifications(
        true,
        submenuItems,
        getContextualMenuItemKey,
        createEmptyItem,
        itemHeight
    );

    return itemsToRender;
}
